import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import FloorMapSvg from "@/components/FloorMapSvg";
import { Users, Clock, MapPin, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatTime } from "@/lib/format";

export default function StaffHostPage() {
    const [reservations, setReservations] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState("");
    const [tables, setTables] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [assigning, setAssigning] = useState(false);

    const loadReservations = useCallback(async () => {
        setRefreshing(true);
        try {
            const { data } = await api.get("/staff/today");
            setReservations(data || []);
            setSelectedRestaurant((prev) => prev || data?.[0]?.restaurant_id || "");
        } catch (error) {
            console.warn("[host] load reservations failed", error?.message);
        } finally {
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setInitialLoading(true);
                const { data } = await api.get("/restaurants");
                if (!active) return;
                setRestaurants(data || []);
                await loadReservations();
            } catch (error) {
                console.warn("[host] initial load failed", error?.message);
            } finally {
                if (active) setInitialLoading(false);
            }
        })();
        const id = setInterval(loadReservations, 6000);
        return () => {
            active = false;
            clearInterval(id);
        };
    }, [loadReservations]);

    useEffect(() => {
        if (!selectedRestaurant) return;
        let active = true;
        (async () => {
            try {
                const { data } = await api.get(`/restaurants/${selectedRestaurant}/tables`);
                if (active) setTables(data || []);
            } catch (error) {
                console.warn("[host] tables load failed", error?.message);
            }
        })();
        return () => {
            active = false;
        };
    }, [selectedRestaurant]);

    const filtered = useMemo(
        () => reservations.filter((r) => !selectedRestaurant || r.restaurant_id === selectedRestaurant),
        [reservations, selectedRestaurant]
    );
    const busyTableIds = useMemo(
        () =>
            filtered
                .filter((r) => ["seated", "checked_in"].includes(r.status) && r.table_id)
                .map((r) => r.table_id),
        [filtered]
    );

    const checkIn = async (id) => {
        try {
            await api.post(`/staff/reservations/${id}/check-in`);
            toast.success("Checked in");
            loadReservations();
        } catch (error) {
            console.warn("[host] check-in failed", error?.message);
            toast.error(error?.response?.data?.detail || "Failed");
        }
    };

    const assignTable = async (reservation, table) => {
        setAssigning(true);
        try {
            await api.post(`/staff/reservations/${reservation.id}/assign-table`, { table_id: table.id });
            toast.success(`${reservation.customer?.name || "Guest"} seated at Table ${table.label}`);
            setSelectedReservation(null);
            loadReservations();
        } catch (error) {
            console.warn("[host] assign table failed", error?.message);
            toast.error(error?.response?.data?.detail || "Failed");
        } finally {
            setAssigning(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
            <Header
                restaurants={restaurants}
                selectedRestaurant={selectedRestaurant}
                onSelectRestaurant={setSelectedRestaurant}
                refreshing={refreshing}
                onRefresh={loadReservations}
            />
            <div className="grid lg:grid-cols-[1fr_460px] gap-6">
                <Card>
                    <CardContent className="p-4">
                        <h2 className="font-display text-xl mb-3">Reservations</h2>
                        <ReservationsTable
                            reservations={filtered}
                            onCheckIn={checkIn}
                            onAssignClick={setSelectedReservation}
                        />
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-display text-xl">Floor</h2>
                            <FloorLegend />
                        </div>
                        <FloorMapSvg tables={tables} busyTableIds={busyTableIds} mode="host" />
                    </CardContent>
                </Card>
            </div>

            <AssignTableSheet
                reservation={selectedReservation}
                tables={tables}
                busyTableIds={busyTableIds}
                assigning={assigning}
                onAssign={assignTable}
                onClose={() => setSelectedReservation(null)}
            />
        </div>
    );
}

function Header({ restaurants, selectedRestaurant, onSelectRestaurant, refreshing, onRefresh }) {
    return (
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
                <h1 className="font-display text-3xl">Host</h1>
                <p className="text-sm text-muted-foreground">Today's reservations + live floor occupancy.</p>
            </div>
            <div className="flex items-center gap-2">
                <Select value={selectedRestaurant} onValueChange={onSelectRestaurant}>
                    <SelectTrigger className="w-56" data-testid="host-restaurant-select">
                        <SelectValue placeholder="Restaurant" />
                    </SelectTrigger>
                    <SelectContent>
                        {restaurants.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                                {r.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={onRefresh} data-testid="host-refresh-button">
                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                </Button>
            </div>
        </div>
    );
}

function ReservationsTable({ reservations, onCheckIn, onAssignClick }) {
    return (
        <div className="overflow-x-auto">
            <Table data-testid="host-reservations-table">
                <TableHeader>
                    <TableRow>
                        <TableHead>Guest</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Party</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Table</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {reservations.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                                No reservations.
                            </TableCell>
                        </TableRow>
                    ) : (
                        reservations.map((r) => (
                            <ReservationRow key={r.id} reservation={r} onCheckIn={onCheckIn} onAssignClick={onAssignClick} />
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

function ReservationRow({ reservation: r, onCheckIn, onAssignClick }) {
    return (
        <TableRow data-testid="host-reservation-row">
            <TableCell>
                <div className="font-medium">{r.customer?.name || "Guest"}</div>
                <div className="text-xs text-muted-foreground">{r.customer?.email}</div>
            </TableCell>
            <TableCell>
                <div className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {formatTime(r.reservation_time)}
                </div>
            </TableCell>
            <TableCell>
                <div className="inline-flex items-center gap-1">
                    <Users className="h-3 w-3" /> {r.party_size}
                </div>
            </TableCell>
            <TableCell>
                <Badge variant="outline" className="capitalize">
                    {r.status.replace("_", " ")}
                </Badge>
            </TableCell>
            <TableCell>{r.table ? `T-${r.table.label}` : "—"}</TableCell>
            <TableCell className="text-right">
                <div className="inline-flex gap-1">
                    {r.status === "booked" && (
                        <Button size="sm" variant="outline" onClick={() => onCheckIn(r.id)} data-testid="host-check-in-button">
                            Check in
                        </Button>
                    )}
                    {["booked", "checked_in"].includes(r.status) && (
                        <Button size="sm" onClick={() => onAssignClick(r)} data-testid="host-assign-table-button">
                            <MapPin className="h-3.5 w-3.5 mr-1" /> Assign
                        </Button>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
}

function FloorLegend() {
    return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="floor-legend">
            <Dot color="hsl(var(--secondary))" /> Available
            <Dot color="hsl(var(--muted))" /> Occupied
        </div>
    );
}

function Dot({ color }) {
    return <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />;
}

function AssignTableSheet({ reservation, tables, busyTableIds, assigning, onAssign, onClose }) {
    return (
        <Sheet open={!!reservation} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-md">
                <SheetHeader>
                    <SheetTitle>Assign a table</SheetTitle>
                </SheetHeader>
                {reservation && (
                    <div className="mt-4">
                        <div className="text-sm">
                            <div className="font-medium">{reservation.customer?.name}</div>
                            <div className="text-xs text-muted-foreground">
                                Party of {reservation.party_size} · {formatTime(reservation.reservation_time)}
                            </div>
                        </div>
                        <div className="mt-4">
                            <FloorMapSvg
                                tables={tables}
                                busyTableIds={busyTableIds}
                                onTableClick={(t) => onAssign(reservation, t)}
                                mode="host"
                            />
                        </div>
                        <p className="text-xs text-muted-foreground mt-3">Tap a table to seat the party.</p>
                        {assigning && (
                            <div className="flex items-center gap-2 text-xs mt-2">
                                <Loader2 className="h-3 w-3 animate-spin" /> Assigning…
                            </div>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
